/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {PoliticalCampaignApiService, PoliticalCampaignModel} from './political-campaign-api.service';
import {PoliticalCampaignEditPageComponent} from './political-campaign-edit-page.component';
import {PoliticalCampaignRoutePageComponent} from './political-campaign-route-page.component';
import politicalCampaignCrumbs from './political-campaign-crumbs';

export function politicalCampaignStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(PoliticalCampaignApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const readOnly = () => !permissions.canEditPoliticalResources();
  const rh = adnRouterHelper.createRouterHelper('political-campaigns.political-campaign', PoliticalCampaignApiService, 'app.pol', 'app');

  states.add(rh.listPath(), rh.list({
    data: {app: 'POLITICAL'},
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: PoliticalCampaignRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    data: {app: 'POLITICAL'},
    resolve: {
      politicalCampaignPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<PoliticalCampaignModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []}, true, readOnly(), api,
            'Political Campaign', 'name', undefined, undefined,
            {objectTypeKey: 'PoliticalCampaign', crumbParams: politicalCampaignCrumbs}
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model, false, readOnly(), api, 'Political Campaign', 'name', undefined, undefined,
          {objectTypeKey: 'PoliticalCampaign', crumbParams: politicalCampaignCrumbs}
        ));
      },
      model: function(politicalCampaignPage: SignalAdminResourcePageState<PoliticalCampaignModel>) {
        return politicalCampaignPage.model();
      }
    },
    views: {
      'content@app': {component: PoliticalCampaignEditPageComponent, bindings: {state: 'politicalCampaignPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'politicalCampaignPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'politicalCampaignPage'}}
    }
  }));
  return states.build();
}
